"use client";

/**
 * Feature-comparison matrix for the pricing page.
 *
 * Client component so it can resolve display copy via `useT()` and follow the
 * visitor's locale — reads the display-only `COMPARISON_GROUPS` config, whose
 * text fields are i18n key paths. Each cell is a check, a dash, or a cautious
 * inline note ("where configured", "1 of Freshdesk / Jira", …) so the table
 * never over-claims a capability. Horizontally scrollable on small screens.
 */

import { Check, Minus } from "lucide-react";
import { Reveal } from "@/components/reveal";
import { useT } from "@/i18n";
import type { T } from "@/i18n/translate";
import {
  COMPARISON_GROUPS,
  PRICING_PLANS,
  type ComparisonCell,
} from "@/lib/marketing-pricing";

function Cell({ value, t }: { value: ComparisonCell; t: T }) {
  if (value === true) {
    return (
      <span className="inline-flex">
        <Check size={16} className="text-status-ok" aria-label="Included" />
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex">
        <Minus size={15} className="text-fg-muted/60" aria-label="Not included" />
      </span>
    );
  }
  // String → an i18n key for an "included with a caveat" note.
  return (
    <span className="text-[11px] font-medium leading-tight text-fg-dim">{t(value)}</span>
  );
}

export function PricingComparison() {
  const t = useT();
  return (
    <Reveal>
      <div className="overflow-x-auto rounded-2xl border border-line-subtle bg-bg-raised">
        <table className="w-full min-w-[760px] border-collapse text-left">
          <thead>
            <tr className="border-b border-line-subtle">
              <th
                scope="col"
                className="sticky left-0 z-10 bg-bg-raised px-5 py-4 text-[12px] font-medium text-fg-muted"
              >
                {t("pricingPage.tiers.featuresColumn")}
              </th>
              {PRICING_PLANS.map((p) => (
                <th
                  key={p.id}
                  scope="col"
                  className={`px-4 py-4 text-center text-[13px] font-semibold ${
                    p.recommended ? "text-brand-300" : "text-fg-bright"
                  }`}
                >
                  {t(p.name)}
                  {p.recommended && (
                    <span className="ml-1.5 align-middle text-[9px] font-semibold uppercase tracking-wider text-brand-300">
                      ★
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARISON_GROUPS.map((group) => (
              <GroupRows key={group.title} title={group.title} rows={group.rows} t={t} />
            ))}
          </tbody>
        </table>
      </div>
    </Reveal>
  );
}

function GroupRows({
  title,
  rows,
  t,
}: {
  title: string;
  rows: (typeof COMPARISON_GROUPS)[number]["rows"];
  t: T;
}) {
  return (
    <>
      <tr className="border-b border-line-subtle bg-bg-elev/60">
        <th
          scope="colgroup"
          colSpan={PRICING_PLANS.length + 1}
          className="sticky left-0 px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.16em] text-fg-muted"
        >
          {t(title)}
        </th>
      </tr>
      {rows.map((row) => (
        <tr
          key={row.label}
          className="border-b border-line-subtle last:border-0 hover:bg-bg-hover/40"
        >
          <td className="sticky left-0 z-10 bg-bg-raised px-5 py-3 align-top">
            <div className="text-[13px] text-fg leading-snug">{t(row.label)}</div>
            {row.caveat && (
              <div className="mt-0.5 text-[11px] text-fg-muted leading-snug">{t(row.caveat)}</div>
            )}
          </td>
          {PRICING_PLANS.map((p) => (
            <td
              key={p.id}
              className={`px-4 py-3 text-center align-middle ${
                p.recommended ? "bg-brand-500/[0.04]" : ""
              }`}
            >
              <Cell value={row.cells[p.id]} t={t} />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
